import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    Users,
    Copy,
    CheckCircle,
    Award,
    TrendingUp,
    Share2,
    Gift,
    Crown,
    Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReferralSection = () => {
    const { user, token } = useAuth();
    const [referralInfo, setReferralInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (user?.user_type === 'agent') {
            fetchReferralInfo();
        }
    }, [user]);

    const fetchReferralInfo = async () => {
        try {
            const response = await axios.get(`${API}/referral/my`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setReferralInfo(response.data);
        } catch (error) {
            console.error('Error fetching referral info:', error);
        } finally {
            setLoading(false);
        }
    };

    const copyReferralCode = () => {
        if (referralInfo?.referral_code) {
            navigator.clipboard.writeText(referralInfo.referral_code);
            setCopied(true);
            toast.success('Code copié !');
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const copyReferralLink = () => {
        if (referralInfo?.referral_code) {
            const link = `${window.location.origin}/register?type=agent&ref=${referralInfo.referral_code}`;
            navigator.clipboard.writeText(link);
            toast.success('Lien de parrainage copié !');
        }
    };

    if (!user || user.user_type !== 'agent') return null;

    if (loading) {
        return (
            <div className="bg-white rounded-xl border border-border/60 p-6 shadow-soft">
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-scalimmo-navy" />
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-border/60 p-6 shadow-soft" data-testid="referral-section">
            <div className="flex items-center gap-2 mb-6">
                <Gift className="w-5 h-5 text-scalimmo-gold" />
                <h2 className="font-heading text-xl font-semibold text-scalimmo-navy">
                    Programme de Parrainage
                </h2>
            </div>

            {!referralInfo?.can_refer ? (
                /* Not Top Performer - Show how to unlock */
                <div className="text-center py-6">
                    <div className="w-16 h-16 bg-scalimmo-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Crown className="w-8 h-8 text-scalimmo-gold" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-scalimmo-navy mb-2">
                        Devenez Top Performer
                    </h3>
                    <p className="text-muted-foreground text-sm max-w-md mx-auto mb-4">
                        Seuls les Top Performers peuvent parrainer de nouveaux agents et gagner en visibilité.
                        Atteignez les critères de certification pour débloquer cette fonctionnalité.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3 text-sm">
                        <Badge variant="outline" className="gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Note ≥ 4.5/5
                        </Badge>
                        <Badge variant="outline" className="gap-1">
                            <CheckCircle className="w-3 h-3" />
                            5+ avis
                        </Badge>
                        <Badge variant="outline" className="gap-1">
                            <CheckCircle className="w-3 h-3" />
                            80%+ réponse
                        </Badge>
                    </div>
                </div>
            ) : (
                /* Top Performer - Show referral tools */
                <div className="space-y-6">
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-muted rounded-lg">
                            <p className="text-2xl font-heading font-semibold text-scalimmo-navy">
                                {referralInfo.referral_count || 0}
                            </p>
                            <p className="text-xs text-muted-foreground">Agents parrainés</p>
                        </div>
                        <div className="text-center p-4 bg-scalimmo-gold/10 rounded-lg">
                            <p className="text-2xl font-heading font-semibold text-scalimmo-gold">
                                +{referralInfo.visibility_boost || 0}
                            </p>
                            <p className="text-xs text-muted-foreground">Boost visibilité</p>
                        </div>
                        <div className="text-center p-4 bg-muted rounded-lg">
                            <p className="text-2xl font-heading font-semibold text-scalimmo-navy">
                                +10
                            </p>
                            <p className="text-xs text-muted-foreground">Par parrainage</p>
                        </div>
                    </div>

                    {/* Referral Code */}
                    <div className="bg-muted rounded-lg p-4">
                        <label className="text-sm font-medium text-muted-foreground mb-2 block">
                            Votre code de parrainage
                        </label>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 bg-white rounded-lg px-4 py-3 font-mono text-lg font-semibold text-scalimmo-navy border border-border">
                                {referralInfo.referral_code}
                            </div>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={copyReferralCode}
                                className="shrink-0"
                                data-testid="copy-code-btn"
                            >
                                {copied ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : (
                                    <Copy className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Share Button */}
                    <Button
                        className="w-full btn-gold"
                        onClick={copyReferralLink}
                        data-testid="share-referral-btn"
                    >
                        <Share2 className="w-4 h-4 mr-2" />
                        Partager le lien d'inscription
                    </Button>

                    {/* How it works */}
                    <div className="border-t border-border pt-4">
                        <h4 className="text-sm font-medium text-scalimmo-navy mb-3">
                            Comment ça marche ?
                        </h4>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li className="flex items-start gap-2">
                                <span className="w-5 h-5 rounded-full bg-scalimmo-gold/20 text-scalimmo-gold flex items-center justify-center text-xs font-bold shrink-0">1</span>
                                Partagez votre code avec des agents immobiliers
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="w-5 h-5 rounded-full bg-scalimmo-gold/20 text-scalimmo-gold flex items-center justify-center text-xs font-bold shrink-0">2</span>
                                Ils s'inscrivent avec votre code de parrainage
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="w-5 h-5 rounded-full bg-scalimmo-gold/20 text-scalimmo-gold flex items-center justify-center text-xs font-bold shrink-0">3</span>
                                Gagnez +10 points de visibilité par parrainage
                            </li>
                        </ul>
                    </div>

                    {/* Referred Agents List */}
                    {referralInfo.referred_agents?.length > 0 && (
                        <div className="border-t border-border pt-4">
                            <h4 className="text-sm font-medium text-scalimmo-navy mb-3 flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                Agents parrainés
                            </h4>
                            <div className="space-y-2">
                                {referralInfo.referred_agents.map((agent) => (
                                    <div
                                        key={agent.id}
                                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                                    >
                                        <div>
                                            <p className="font-medium text-scalimmo-navy text-sm">
                                                {agent.first_name} {agent.last_name}
                                            </p>
                                            {agent.agency_name && (
                                                <p className="text-xs text-muted-foreground">{agent.agency_name}</p>
                                            )}
                                        </div>
                                        <Badge variant="secondary" className="text-xs">
                                            +10 boost
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Referral Code Input for Registration
export const ReferralCodeInput = ({ value, onChange, onValidate }) => {
    const [validating, setValidating] = useState(false);
    const [referrer, setReferrer] = useState(null);
    const [error, setError] = useState(null);

    const validateCode = async (code) => {
        if (!code || code.length < 8) {
            setReferrer(null);
            setError(null);
            return;
        }

        setValidating(true);
        setError(null);

        try {
            const response = await axios.get(`${API}/referral/validate/${code}`);
            setReferrer(response.data.referrer);
            onValidate?.(true);
        } catch (err) {
            setReferrer(null);
            setError(err.response?.data?.detail || 'Code invalide');
            onValidate?.(false);
        } finally {
            setValidating(false);
        }
    };

    const handleChange = (e) => {
        const code = e.target.value.toUpperCase();
        onChange(code);
        if (code.length >= 8) {
            validateCode(code);
        } else {
            setReferrer(null);
            setError(null);
        }
    };

    return (
        <div className="space-y-2" data-testid="referral-code-input">
            <div className="relative">
                <Input
                    placeholder="Code de parrainage (optionnel)"
                    value={value}
                    onChange={handleChange}
                    maxLength={8}
                    className="uppercase font-mono"
                    data-testid="input-referral-code"
                />
                {validating && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
                {referrer && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600" />
                )}
            </div>

            {referrer && (
                <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg text-sm">
                    <Award className="w-4 h-4 text-scalimmo-gold" />
                    <span className="text-green-700">
                        Parrainé par <strong>{referrer.first_name} {referrer.last_name}</strong>
                        {referrer.agency_name && ` (${referrer.agency_name})`}
                    </span>
                </div>
            )}

            {error && (
                <p className="text-sm text-destructive">{error}</p>
            )}
        </div>
    );
};
